<template>
  <div>
    <v-card class="pa-2 ma-4 secondary--text text-subtitle-1 pa-4">
      <div>
        This page is used for administrative management of the
        storage system of this device. Please use with caution.
      </div>
      <v-btn
        color="secondary"
        @click="confirmClearLocalStorage()"
        class="mt-3"
      >
        <v-icon>mdi-database</v-icon>
        <span>Clear Local Storage</span>
      </v-btn>
    </v-card>
    <v-expansion-panels class="px-4">
        <v-expansion-panel>
          <v-expansion-panel-header>Farm-Line-MG</v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-data-table
              mobile-breakpoint="300"
              xxdisable-sort
              dense
              :headers="flmHeaders"
              :items="settings.farmLineMg"
              item-key="name"
              class="elevation-1"
              :search="searchFarmLineMg"
              :item-class="itemRowBackground"
            >
              <template v-slot:top>
                <v-text-field
                  v-model="searchFarmLineMg"
                  label="Search"
                  class="mx-4"
                />
              </template>
            </v-data-table>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel>
          <v-expansion-panel-header>QAP Health Batches ({{ settings.currentFarm }})</v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-data-table
              mobile-breakpoint="300"
              disable-sort
              dense
              :headers="qapHeaders"
              :items="farmBatches"
              item-key="name"
              class="elevation-1"
              :search="searchFarmBatchInfo"
            >
              <template v-slot:top>
                <v-text-field
                  v-model="searchFarmBatchInfo"
                  label="Search"
                  class="mx-4"
                />
              </template>
            </v-data-table>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel>
          <v-expansion-panel-header>Archived Genomic Groups ({{this.archivedGenomicBleedGroups.length}})</v-expansion-panel-header>
          <v-expansion-panel-content>
            <div class="primary--text">* Archived Genomic Groups older than 45 days are automatically deleted.</div>
            <v-text-field v-model="searchGenomicArchive" clearable label="Search" />
            <div class="d-flex flex-wrap my-auto">
              <div v-if="archivedGenomicBleedGroups.length == 0" class="text-subtitle-2">No Archived Genomic Groups exist.</div>
              <v-card width="260px" 
                class="mr-2 mb-2" 
                outlined 
                v-for="(group,gndx) in filteredArchiveGenomicGroups" 
                :key="group.groupName + group.uploadDate">
                <v-card-title class="text-subtitle-2">
                    <div style="cursor: pointer" @click="toggleGroupDetail(showGroupDetail.genomic, gndx)">
                      <b class="mr-2">{{group.groupName }}</b>
                      <v-icon v-if="showGroupDetail.genomic[gndx]">mdi-chevron-up</v-icon>
                      <v-icon v-else>mdi-chevron-down</v-icon>
                    </div>
                    <div v-show="showGroupDetail.genomic[gndx]">
                      <div v-for="action in sortGroupActionDates(group)" :key="action.type" class="ml-2">
                        <div v-if="action.date != null">
                          <v-icon class="px-0 pb-1 mr-2">{{ action.icon }}</v-icon>
                          <span class="pb-0">{{ formatDate(action.date) }}</span>
                        </div>
                      </div>
                    </div>
                </v-card-title>
                <div class="px-4">
                  <v-btn 
                    color="success" 
                    class="my-2" 
                    @click="showViewGenomicDialog(group)"
                  >
                    <v-icon>mdi-eye-outline</v-icon><div class="text-subtitle-2"></div>
                  </v-btn>
                  <v-btn 
                    color="primary" 
                    class="my-2 mx-2" 
                    @click="confirmRestoreGenomicGroup(group)"
                  >
                    <v-icon>mdi-file-restore-outline</v-icon>
                  </v-btn>
                  <v-btn 
                    color="secondary" 
                    class="my-2" 
                    @click="confirmDeleteGenomicGroup(group)"
                  >
                    <v-icon>mdi-delete-outline</v-icon>
                  </v-btn>
                </div>
              </v-card>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel>
          <v-expansion-panel-header>Archived Health Groups ({{this.archivedHealthBleedGroups.length}})</v-expansion-panel-header>
          <v-expansion-panel-content>
            <div class="primary--text">* Archived Health Groups older than 45 days are automatically deleted.</div>
            <v-text-field v-model="searchHealthArchive" clearable label="Search" />
            <div class="d-flex flex-wrap my-auto">
              <div v-if="archivedHealthBleedGroups.length == 0" class="text-subtitle-2">No Archived Health Groups exist.</div>
              <v-card width="260px" 
                class="mr-2 mb-2" 
                outlined 
                v-for="(group,gndx) in filteredArchiveHealthGroups" :key="group.groupName + group.uploadDate">
                <v-card-title class="text-subtitle-2">
                    <div style="cursor: pointer" @click="toggleGroupDetail(showGroupDetail.health,gndx)">
                      <b class="mr-2">{{group.groupName }}</b>
                      <v-icon v-if="showGroupDetail.health[gndx]">mdi-chevron-up</v-icon>
                      <v-icon v-else>mdi-chevron-down</v-icon>
                    </div>
                    <div v-show="showGroupDetail.health[gndx]">
                      <div v-for="action in sortGroupActionDates(group)" :key="action.type" class="ml-2">
                        <div v-if="action.date != null">
                          <v-icon class="px-0 pb-1 mr-2">{{ action.icon }}</v-icon>
                          <span class="pb-0">{{ formatDate(action.date) }}</span>
                        </div>
                      </div>
                    </div>
                </v-card-title>
                <div class="px-4">
                  <v-btn 
                    color="success"
                    class="my-2" 
                    @click="showViewHealthDialog(group)"
                  >
                    <v-icon>mdi-eye-outline</v-icon><div class="text-subtitle-2"></div>
                  </v-btn>
                  <v-btn 
                    color="primary" 
                    class="my-2 mx-2" 
                    @click="confirmRestoreHealthGroup(group, restoreArchivedHealthGroupDialog)"
                  >
                    <v-icon>mdi-file-restore-outline</v-icon>
                  </v-btn>
                  <v-btn 
                    color="secondary" 
                    class="my-2" 
                    @click="confirmDeleteHealthGroup(group)"
                  >
                    <v-icon>mdi-delete-outline</v-icon>
                  </v-btn>
                </div>
              </v-card>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
    </v-expansion-panels>
 
    <v-dialog v-model="clearLocalStorageDialog" persistent max-width="350">
      <v-card class="pb-2" color="cardbackground">
        <v-card-title class="text-h5">Clear Local Storage</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="3">
              <v-icon x-large color="secondary">mdi-alert-outline</v-icon>
            </v-col>
            <v-col cols="9">
              Clearing local storage will delete all GBA data on your device.
              Are you sure you want to do this?
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <div class="flex-grow-1"></div>
          <v-btn color="primary" class="white--text" @click="clearLocalStorage()">Yes</v-btn>
          <v-btn color="secondary" @click="clearLocalStorageDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteGenomicGroupDialog" persistent max-width="350">
      <v-card v-if="this.groupToDelete != null" class="pb-2" color="cardbackground">
        <div class="pa-4 text-h6 text-wrap">Delete Archived Genomic Group</div>
        <v-card-text>
          {{this.groupToDelete.groupName}}<br />{{formatDate(this.groupToDelete.uploadDate)}}
          <br /><br />Are you sure you want to delete this group?
        </v-card-text>
        <v-card-actions>
          <div class="flex-grow-1"></div>
          <v-btn color="primary" class="white--text" @click="deleteGenomicArchiveGroup()">Yes</v-btn>
          <v-btn color="secondary" outlined @click="deleteGenomicGroupDialog = null">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteHealthGroupDialog" persistent max-width="350">
      <v-card v-if="this.groupToDelete != null" class="pb-2" color="cardbackground">
        <v-card-title class="text-h6">Delete Archived Health Group</v-card-title>
        <v-card-text>
          {{this.groupToDelete.groupName}}<br />{{formatDate(this.groupToDelete.uploadDate)}}
          <br /><br />Are you sure you want to delete this group?
        </v-card-text>
        <v-card-actions>
          <div class="flex-grow-1"></div>
          <v-btn color="primary" class="white--text" @click="deleteHealthArchiveGroup()">Yes</v-btn>
          <v-btn color="secondary" outlined @click="deleteHealthGroupDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="restoreArchivedGenomicGroupDialog" persistent max-width="350">
      <v-card class="pb-2" color="cardbackground">
        <v-card-title class="text-h5">Restore Genomic Group</v-card-title>
        <v-card-text>
          <div v-if="this.groupToRestore != null">
            {{this.groupToRestore.groupName}}<br />{{formatDate(this.groupToRestore.uploadDate)}}
            <br /><br />Are you sure you want to restore this group?
          </div>  
        </v-card-text>
        <v-card-actions>
          <div class="flex-grow-1"></div>
          <v-btn color="primary" class="white--text" @click="restoreArchivedGenomicGroup()">Yes</v-btn>
          <v-btn color="secondary" outlined @click="restoreArchivedGenomicGroupDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

     <v-dialog v-model="restoreArchivedHealthGroupDialog" persistent max-width="350">
      <v-card class="pb-2" color="cardbackground">
        <v-card-title class="text-h5">Restore Health Group</v-card-title>
        <v-card-text>
          <div v-if="this.groupToRestore != null">
            {{this.groupToRestore.groupName}}<br />{{formatDate(this.groupToRestore.uploadDate)}}
            <br /><br />Are you sure you want to restore this group?
          </div>  
        </v-card-text>
        <v-card-actions>
          <div class="flex-grow-1"></div>
          <v-btn color="primary" class="white--text" @click="restoreArchivedHealthGroup()">Yes</v-btn>
          <v-btn color="secondary" outlined @click="restoreArchivedHealthGroupDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="viewArchivedGenomicGroupDialog" persistent max-width="380">
      <v-card v-if="viewArchivedGenomicGroupDialog" class="pb-2" color="cardbackground">
        <v-card-text class="pa-2">
          <div class="primary--text title pl-2 py-2">{{groupToView?groupToView.groupName:''}}</div>
          <div v-if="viewArchivedGroupDetails">
            <v-simple-table
              class="mx-1"
              v-if="groupToView"
              :fixed-header="true"
              :height="190"
              dense
            >
              <thead>
                <tr>
                  <th class="px-1 caption">Tube</th>
                  <th class="px-1 caption">Wingband</th>
                  <th class="px-1 caption">Date</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in groupToView.samples" :key="item.tube">
                  <td class="px-1 caption">{{item.tube}}</td>
                  <td class="px-1 caption">{{item.wingband}}</td>
                  <td class="px-1 caption" nowrap>{{item.date}}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>
          <div v-else class="pt-0">
            <ul class="font-weight-medium">
              <li><b>Operator:</b> {{groupToView?groupToView.operator:''}}</li>
              <li><b>Farm:</b> {{groupToView?groupToView.farm:''}}</li>
              <li><b>Line:</b> {{groupToView?groupToView.line:''}}</li>
              <li><b>Disp:</b> {{groupToView?groupToView.disp:''}}</li>
              <li><b>Hatch:</b> {{groupToView?groupToView.hatch:''}}</li>
              <li><b>Mini-Gen:</b> {{groupToView?groupToView.minigeneration:''}}</li>
              <li><b>TR/EX:</b> {{groupToView?groupToView.trex:''}}</li>
              <li><b>Gender:</b> {{groupToView?groupToView.gender:''}}</li>
              <li><b>Notes:</b> {{groupToView?groupToView.notes:''}}</li>
            </ul>
            <div style="clear: both;"></div>
          </div>
        </v-card-text>
        <v-card-actions v-if="viewArchivedGroupDetails">
          <div class="flex-grow-1"></div>
          <div class="mr-1 caption text-left">Count: {{groupToView?groupToView.samples.length:''}}</div>
          <v-spacer />
          <v-spacer />
          <v-btn color="primary" outlined @click="viewArchivedGroupDetails = false">Summary</v-btn>
          <v-btn color="secondary" outlined @click="viewArchivedGenomicGroupDialog = false; viewArchivedGroupDetails = false">Close</v-btn>
        </v-card-actions>
        <v-card-actions v-else class="pt-0">
            <div class="flex-grow-1"></div>
              <v-btn color="primary" outlined @click="viewArchivedGroupDetails = true">Details</v-btn>
            <v-btn color="secondary" outlined @click="viewArchivedGenomicGroupDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="viewArchivedHealthGroupDialog" persistent max-width="500">
      <v-card v-if="viewArchivedHealthGroupDialog" class="pb-2" color="cardbackground">
        <v-card-text class="px-2 pb-2">
          <div class="primary--text title pl-2 py-2">{{groupToView?groupToView.groupName:''}}</div>
          <div v-if="viewArchivedGroupDetails">
            <v-simple-table
              v-if="groupToView"
              :fixed-header="true"
              :height="180"
              dense
            >
              <thead>
                <tr>
                  <th class="px-1 caption">Plate</th>
                  <th class="px-1 caption">{{groupToView.setup=='Tube'?'Tube':'Well'}}</th>
                  <th class="px-1 caption">Wingband</th>
                  <th class="px-1 caption">Line</th>
                  <th class="px-1 caption">MG</th>
                  <th class="px-1 caption">AirSpace</th>
                  <th class="px-1 caption">Flock</th>
                  <th class="px-1 caption">Date</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in groupToView.wells" :key="item.wingband">
                  <td class="px-1 caption">{{item.plateNumber}}</td>
                  <td class="px-1 caption">{{groupToView.setup == 'Tube'? item.tubeNumber : item.wellNumber}}</td>
                  <td class="px-1 caption">{{item.wingband}}</td>
                  <td class="px-1 caption">{{item.line}}</td>
                  <td class="px-1 caption">{{item.mg}}</td>
                  <td class="px-1 caption">{{item.airSpace}}</td>
                  <td class="px-1 caption">{{item.flock}}</td>
                  <td class="px-1 caption" nowrap>{{item.date}}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>
          <div v-else>
            <ul>
              <li><b>Farm:</b> {{groupToView?groupToView.farm:''}}</li>
              <li><b>Purpose:</b> {{groupToView?groupToView.purpose:''}}</li>
              <li><b>Hatch:</b> {{groupToView?groupToView.hatch:''}}</li>
              <li><b>Age:</b> {{groupToView?groupToView.age:''}}</li>
              <li><b>Setup:</b> {{groupToView?groupToView.setup:''}}</li>
            </ul>
            <div style="clear: both;"></div>
          </div>
        </v-card-text>
        <v-card-actions v-if="viewArchivedGroupDetails">
          <div class="ml-2 mr-1 caption text-left">Count: {{groupToView?groupToView.wells.length:''}}</div>
          <v-spacer />
          <v-spacer />
          <v-btn color="primary" outlined @click="viewArchivedGroupDetails = false">Summary</v-btn>
          <v-btn color="secondary" outlined @click="viewArchivedHealthGroupDialog = false; viewArchivedGroupDetails = false">Close</v-btn>
        </v-card-actions>
        <v-card-actions v-else class="pt-3">
            <div class="flex-grow-1"></div>
              <v-btn color="primary" outlined @click="viewArchivedGroupDetails = true">Details</v-btn>
            <v-btn color="secondary" outlined @click="viewArchivedHealthGroupDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import { mapState } from 'vuex'
import OfflineStorage from '../offlineStorage'

//xxx
import { REASON_MAPPING } from '../views/HealthBleed.vue';

export default {
  components: {
  },
  data() {
    return {
      archivedGenomicBleedGroups: [],
      archivedHealthBleedGroups: [],

      clearLocalStorageDialog: false,

      deleteGenomicGroupDialog: false,
      deleteHealthGroupDialog: false,
      restoreArchivedHealthGroupDialog: false,
      restoreArchivedGenomicGroupDialog: false,
      viewArchivedGenomicGroupDialog: false,
      viewArchivedHealthGroupDialog: false,
      viewArchivedGroupDetails: false,
      searchFarmLineMg: '',
      searchFarmBatchInfo: '',
      searchGenomicArchive: '',
      searchHealthArchive: '',

      showGroupDetail: {genomic: [],health:[]},

      groupToDelete: null,
      groupToRestore: null,
      groupToView: null,
    }
  },
  async created() {
    try {
      this.archivedGenomicBleedGroups = await OfflineStorage.getItem('archivedGenomicBleedGroups');
      if (this.archivedGenomicBleedGroups == null) this.archivedGenomicBleedGroups = [];

      this.archivedHealthBleedGroups = await OfflineStorage.getItem('archivedHealthBleedGroups');
      if (this.archivedHealthBleedGroups == null) this.archivedHealthBleedGroups = [];
    } catch(error) {
      this.$root.showNoticeError(error.message);
    }
  },
  computed: {
    ...mapState(['settings', 'isOnline']),
    breakpoint() {
      return this.$vuetify.breakpoint.name;
    },
    farmBatches() {

      // Internal function used to map reasons to their corresponding
      // types using REASON_MAPPING (from HealthBleed.vue)
      const getTypesFromReasons = (reasons) => {
        return reasons.split(', ').map(reason => {
          const mapping = REASON_MAPPING.find(mapping => mapping.qapReason === reason);
          return mapping ? mapping.gbaType : 'Other';
        }).join(', ');
      };

      // Map over each batch in farmBatchInfo to create a new flattened array of batch objects
      const result = this.settings.farmBatchInfo.map(batch => {
        const uniqueValues = (key) => [...new Set(batch.samples.map(sample => sample[key]).join(', ').split(', '))].join(', ');
        const batchObj = {
          batchNum: batch.batchNum,
          schedule: batch.schedule,
          plannedCollectionDate: batch.plannedCollectionDate,
          sampleCount: batch.samples.length,
          reason: uniqueValues('reason'),
          purpose: uniqueValues('purpose'),
          type: getTypesFromReasons(uniqueValues('reason')),
          flocks: uniqueValues('flocks'),
          lines: uniqueValues('lines'),
          house: uniqueValues('house'),
          location: uniqueValues('location'),
          age: uniqueValues('age')
        };
        return batchObj;
      });

      return result;
    },
    flmHeaders() {
      const tableCols = [
        {
          text: 'Farm',
          align: 'start',
          xxxsortable: false,
          value: 'farm',
        },
        {
          text: 'Line',
          value: 'line',
        },
        { text: 'MGStart', align: 'right', value: 'mgStart' },
        { text: 'MGEnd', align: 'right', value: 'mgEnd' },
        { text: 'Start Date', value: 'mgStartDate', width: this.breakpoint === 'xs'? '115px': ''},
        { text: 'End Date', value: 'mgEndDate', width: this.breakpoint === 'xs'? '115px': '' }
      ];
      return tableCols;
    },
    qapHeaders() {
      const tableCols = [
        {
          text: 'Batch Id',
          align: 'start',
          value: 'batchNum',
          width: '125px'
        },
        { text: 'Schedule', value: 'schedule', },
        { text: 'Planned', value: 'plannedCollectionDate', width: '110px' },
        { text: 'Samples', value: 'sampleCount', align: 'center'},
        { text: 'Purpose', value: 'purpose' },
        { text: 'Reason', value: 'reason' },
        { text: 'GBA Type', value: 'type' },
        { text: 'Flocks', value: 'flocks' },
        { text: 'Lines', value: 'lines', width: '250px' },
        { text: 'Locations', value: 'location' },
        { text: 'Ages', value: 'age', align: 'center'},
        { text: 'Houses', value: 'house'}
      ];
      return tableCols;
    },
    filteredArchiveGenomicGroups() {
      if (this.searchGenomicArchive == null || this.searchGenomicArchive.length===0) return this.archivedGenomicBleedGroups;
      var filteredGroups = this.archivedGenomicBleedGroups.filter(bs => bs.groupName.includes(this.searchGenomicArchive.toUpperCase()) 
            || this.formatDate(bs.uploadDate).includes(this.searchGenomicArchive.toUpperCase())
      );
      return filteredGroups;
    },
    filteredArchiveHealthGroups() {
      if (this.searchHealthArchive == null || this.searchHealthArchive.length===0) return this.archivedHealthBleedGroups;
      var filteredGroups = this.archivedHealthBleedGroups.filter(hs => hs.groupName.includes(this.searchHealthArchive.toUpperCase()) 
            || this.formatDate(hs.uploadDate).includes(this.searchHealthArchive.toUpperCase())
      );
      return filteredGroups;
    }
  },
  methods: {
    confirmClearLocalStorage() {
      if (!this.isOnline) {
        const msg = "Must be on-line to clear Local Storage.";
        this.$root.showNoticeError(msg);
      }
      else this.clearLocalStorageDialog = true;
    },
    confirmDeleteGenomicGroup(item) {
      this.groupToDelete = item;
      this.deleteGenomicGroupDialog = true;
    },
    confirmDeleteHealthGroup(item) {
      this.groupToDelete = item;
      this.deleteHealthGroupDialog = true;
    },
    confirmRestoreGenomicGroup(item) {
      this.groupToRestore = item;
      this.restoreArchivedGenomicGroupDialog = true;
    },
    confirmRestoreHealthGroup(item) {
      this.groupToRestore = item;
      this.restoreArchivedHealthGroupDialog = true;
    },
    async clearLocalStorage() {
      try {
        if (!this.isOnline) {
          this.clearLocalStorageDialog = false;
          throw new Error("Must be on-line to clear Local Storage.");
        }
        await OfflineStorage.clear();
        window.location = process.env.BASE_URL;
      } catch (error) {
        this.$root.showNoticeError(error.message);
      }    
    },
    async deleteGenomicArchiveGroup() {
      const index = this.archivedGenomicBleedGroups.findIndex(x => x.groupName === this.groupToDelete.groupName 
                                                                && x.uploadDate == this.groupToDelete.uploadDate);
      this.archivedGenomicBleedGroups.splice(index, 1);
      await OfflineStorage.setItem('archivedGenomicBleedGroups', this.archivedGenomicBleedGroups);
      this.$root.showNoticeSuccess('Genomic Group Deleted Successfully');
      this.deleteGenomicGroupDialog = false;
      this.groupToDelete = null;  
    },
    async deleteHealthArchiveGroup() {
      const index = this.archivedHealthBleedGroups.findIndex(x => x.groupName === this.groupToDelete.groupName 
                                                               && x.uploadDate == this.groupToDelete.uploadDate);
      this.archivedHealthBleedGroups.splice(index, 1);
      await OfflineStorage.setItem('archivedHealthBleedGroups', this.archivedHealthBleedGroups);
      this.$root.showNoticeSuccess('Health Group Deleted Successfully');
      this.deleteHealthGroupDialog = false;
      this.groupToDelete = null;  
    },
    formatDate(dt) {
      let fdt = new Date(dt).toLocaleString();
      fdt = fdt.replace(',','');
      return fdt;
    },
    itemRowBackground(item) {
      var dtEnd = new Date(item.mgEndDate);
      var dtStart = new Date(item.mgStartDate);
      var now = new Date();
      return dtStart <= now && dtEnd >= now ? '' : 'grey--text';
    },
    async restoreArchivedGenomicGroup() {
      const index = this.archivedGenomicBleedGroups.findIndex(x => x.groupName === this.groupToRestore.groupName
                                                                && x.uploadDate == this.groupToRestore.uploadDate);
      this.archivedGenomicBleedGroups[index].restoreDate = new Date();

      let genomicBleedGroups = await OfflineStorage.getItem('genomicBleedGroups');
      if (genomicBleedGroups == null) genomicBleedGroups = [];
      genomicBleedGroups.push(this.archivedGenomicBleedGroups[index]);
      await OfflineStorage.setItem('genomicBleedGroups', genomicBleedGroups);

      this.archivedGenomicBleedGroups.splice(index, 1);
      await OfflineStorage.setItem('archivedGenomicBleedGroups', this.archivedGenomicBleedGroups);

      this.$root.showNoticeSuccess('Genomic Group Restored Successfully.');

      this.restoreArchivedGenomicGroupDialog = false;  
    },
    async restoreArchivedHealthGroup() {
      const index = this.archivedHealthBleedGroups.findIndex(x => x.groupName === this.groupToRestore.groupName
                                                               && x.uploadDate == this.groupToRestore.uploadDate);
      this.archivedHealthBleedGroups[index].restoreDate = new Date();

      let healthBleedGroups = await OfflineStorage.getItem('healthBleedGroups');
      healthBleedGroups.push(this.archivedHealthBleedGroups[index]);
      await OfflineStorage.setItem('healthBleedGroups', healthBleedGroups);

      this.archivedHealthBleedGroups.splice(index, 1);
      await OfflineStorage.setItem('archivedHealthBleedGroups', this.archivedHealthBleedGroups);

      this.$root.showNoticeSuccess('Health Group Restored Successfully.');
      this.restoreArchivedHealthGroupDialog = false;  
    },
    showViewGenomicDialog(group) {
      const item = this.archivedGenomicBleedGroups.find(x => x.groupName === group.groupName
                                                          && x.uploadDate === group.uploadDate);
      this.groupToView = item;
      this.viewArchivedGenomicGroupDialog = true;
    },
    showViewHealthDialog(group) {
      const item = this.archivedHealthBleedGroups.find(x => x.groupName === group.groupName
                                                         && x.uploadDate === group.uploadDate);
      this.groupToView = item;
      this.viewArchivedHealthGroupDialog = true;
    },
    sortGroupActionDates(group) {
      const actions = [
        {icon:'mdi-upload',date: group.uploadDate},
        {icon:'mdi-file-restore-outline',date: group.restoreDate},
        {icon: 'mdi-delete-outline', date: group.deleteDate}
      ];
      const sortedActions = actions.sort((a,b) => {
        if (a.date == null)
          return -1;
        if (b.date == null) 
          return 1;
        else 
          return a.date - b.date;
      });
      return sortedActions;
    },
    toggleGroupDetail(group, index) {
      group[index] = !group[index];
      this.$forceUpdate();
    }
  }
}
</script>
<style scoped>
  .side-by-side li {
    display: block;
    float: left;
    width: 140px;
    margin: 2px;
  }
</style>